import React, { useRef } from 'react';
import { Switch, Tooltip } from '@mui/material';

import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import TagIcon from '@mui/icons-material/Tag';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';

import { Button } from 'containers/Tiptap/components/tiptap-ui-primitive/button';
import { RowControlCell, RowHelp, RowLabel, RowLabelCell } from 'components/Entities/DocumentSettingsTab/styles';
import { HEADER_FOOTER_DETAILS, HeaderFooterDetailId } from 'containers/Tiptap/lib/header-footer-variables';

import {
  StyledHiddenFileInput,
  StyledInsertList,
  StyledPanelSection,
  StyledPanelSectionHeading,
  StyledPanelSettingRow,
} from './styles';

const PanelSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <StyledPanelSection>
    <StyledPanelSectionHeading>{title}</StyledPanelSectionHeading>
    {children}
  </StyledPanelSection>
);

interface InsertActionProps {
  icon: React.ReactElement;
  label: string;
  testId: string;
  onClick: () => void;
  disabledReason?: string;
}

const InsertAction: React.FC<InsertActionProps> = ({ icon, label, testId, onClick, disabledReason }) => (
  <Tooltip title={disabledReason ?? ''} placement="left">
    <span>
      <Button
        type="button"
        data-style="ghost"
        aria-label={label}
        data-testid={testId}
        disabled={Boolean(disabledReason)}
        onMouseDown={(event) => event.preventDefault()}
        onClick={onClick}
      >
        {icon}
        <span className="tiptap-button-text">{label}</span>
      </Button>
    </span>
  </Tooltip>
);

const NO_LOGO_TOOLTIP = 'No company logo yet. Ask your admin to add one in Company settings.';

// Sized and coloured by the Tiptap button's icon class, as in the editor's menus.
const ICON_PROPS = { className: 'tiptap-button-icon', fontSize: 'small' } as const;

interface PanelSwitchRowProps {
  label: string;
  help: string;
  testId: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}

const PanelSwitchRow: React.FC<PanelSwitchRowProps> = ({ label, help, testId, checked, onChange }) => {
  const inputProps = { 'aria-label': label, 'data-testid': testId };
  return (
  <StyledPanelSettingRow>
    <RowLabelCell>
      <RowLabel>{label}</RowLabel>
      <RowHelp>{help}</RowHelp>
    </RowLabelCell>
    <RowControlCell>
      <Switch
        size="small"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        slotProps={{ input: inputProps }}
      />
    </RowControlCell>
  </StyledPanelSettingRow>
  );
};

interface LayoutSectionProps {
  differentFirstPage: boolean;
  onDifferentFirstPageChange: (value: boolean) => void;
}

export const HeaderFooterLayoutSection: React.FC<LayoutSectionProps> = ({
  differentFirstPage,
  onDifferentFirstPageChange,
}) => (
  <PanelSection title="Layout">
    <PanelSwitchRow
      label="Different first page"
      help="Page 1 has its own header and footer."
      testId="header-footer-different-first-page"
      checked={differentFirstPage}
      onChange={onDifferentFirstPageChange}
    />
  </PanelSection>
);

interface InsertSectionProps {
  hasCompanyLogo: boolean;
  onInsertImage: (file: File) => void;
  onInsertCompanyLogo: () => void;
}

export const HeaderFooterInsertSection: React.FC<InsertSectionProps> = ({
  hasCompanyLogo,
  onInsertImage,
  onInsertCompanyLogo,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <PanelSection title="Insert">
      <StyledInsertList>
        <InsertAction icon={<ImageOutlinedIcon {...ICON_PROPS} />} label="Insert image" testId="header-footer-insert-image" onClick={() => fileInputRef.current?.click()} />
        <InsertAction icon={<BusinessOutlinedIcon {...ICON_PROPS} />} label="Insert company logo" testId="header-footer-insert-logo" onClick={onInsertCompanyLogo} disabledReason={hasCompanyLogo ? undefined : NO_LOGO_TOOLTIP} />
        <StyledHiddenFileInput
          ref={fileInputRef}
          type="file"
          accept="image/*"
          aria-hidden
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) onInsertImage(file);
            event.target.value = '';
          }}
        />
      </StyledInsertList>
    </PanelSection>
  );
};

const VARIABLE_ICONS: Record<HeaderFooterDetailId, React.ReactElement> = {
  page: <TagIcon {...ICON_PROPS} />,
  total: <LibraryBooksOutlinedIcon {...ICON_PROPS} />,
  documentName: <DescriptionOutlinedIcon {...ICON_PROPS} />,
  documentVersion: <HistoryOutlinedIcon {...ICON_PROPS} />,
  pageOfTotal: <ArticleOutlinedIcon {...ICON_PROPS} />,
};

export const HeaderFooterDetailsSection: React.FC<{ onInsert: (token: string) => void }> = ({ onInsert }) => (
  <PanelSection title="Page and document details">
    <StyledInsertList>
      {HEADER_FOOTER_DETAILS.map(({ id, label, token }) => (
        <InsertAction
          key={id}
          icon={VARIABLE_ICONS[id]}
          label={`Insert ${id === 'pageOfTotal' ? `“${label}”` : label.toLowerCase()}`}
          testId={`header-footer-insert-${id}`}
          onClick={() => onInsert(token)}
        />
      ))}
    </StyledInsertList>
  </PanelSection>
);
